// src/pages/Main/frontCarousel.js

import React, { Component } from "react";
import FrontShot, { ThemedShotView, focusPicture, reducedMotion, shownShots } from "pages/Main/frontShot";
import "pages/Main/frontCarousel.css";

// How long a slide stays before the next; the dot in use fills over the same time (`--fp-dwell`).
export const DWELL = 6000;
// A horizontal drag longer than this, in CSS pixels, is a swipe rather than a click.
const SWIPE = 40;

/**
 * An app's pictures, one at a time in the card's 16:10 box, with a caption under it that follows the slide. It
 * advances every DWELL ms; hovering it, focusing it or opening a picture at full size pauses it; choosing a slide
 * (an arrow, a dot, ← → or a swipe) stops it for good; under reduced motion it never advances by itself.
 * Spec: docs/specs/2026-10-17-front-carousel-lightbox.md#2--carousel-for-an-apps-pictures
 * @param shots [{ name, alt, caption }], two or more; one with no picture is left out
 * @param label the carousel's accessible name
 */
export default class Carousel extends Component {
  constructor(props) {
    super(props);
    // `cycle` counts every slide shown, so a dwell restarts even when the same index comes round again.
    this.state = {
      index: 0, cycle: 0, auto: !reducedMotion(), hovered: false, focused: false, viewOpen: false, viewIndex: 0,
    };
    this.root = React.createRef();
    this.timer = null;
    this.remaining = DWELL;
    this.started = 0;
    this.downX = null;
    this.swiped = false;
  }

  componentDidMount() {
    if (this.running()) {
      this.start();
    }
  }

  // A new slide starts a whole dwell; a pause keeps what is left of it, as the dot's fill keeps its place.
  componentDidUpdate(prevProps, prevState) {
    const wasRunning = this.running(prevState);
    const running = this.running();
    if (prevState.cycle !== this.state.cycle) {
      clearTimeout(this.timer);
      this.remaining = DWELL;
      if (running) {
        this.start();
      }
    } else if (running && !wasRunning) {
      this.start();
    } else if (!running && wasRunning) {
      clearTimeout(this.timer);
      this.remaining -= Date.now() - this.started;
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  running(state = this.state) {
    return state.auto && !state.hovered && !state.focused && !state.viewOpen && this.shots().length > 1;
  }

  shots() {
    return shownShots(this.props.shots);
  }

  start() {
    this.started = Date.now();
    this.timer = setTimeout(this.advance, Math.max(0, this.remaining));
  }

  wrap(index) {
    const count = this.shots().length;
    return (index + count) % count;
  }

  advance = () => {
    this.setState(({ index, cycle }) => ({ index: this.wrap(index + 1), cycle: cycle + 1 }));
  };

  choose = (index) => {
    this.setState(({ cycle }) => ({ index: this.wrap(index), cycle: cycle + 1, auto: false }));
  };

  open = (index) => {
    // The click that ends a swipe is not a request for the full-size view.
    if (this.swiped) {
      this.swiped = false;
      return;
    }
    this.setState({ viewOpen: true, viewIndex: index });
  };

  // Moving through the pictures at full size is choosing one: the carousel comes back on it, and stays there.
  closeView = () => {
    this.setState(({ index, viewIndex, cycle }) => (viewIndex === index
      ? { viewOpen: false }
      : { viewOpen: false, index: viewIndex, cycle: cycle + 1, auto: false }));
  };

  slideButton(index) {
    const slide = this.root.current.querySelectorAll(".fp-carousel-slide")[index];
    return slide ? slide.querySelector("button") : null;
  }

  focusSlide = () => focusPicture(this.slideButton(this.state.index));

  // Only the slide in view is a thumbnail the view can grow from or shrink to; the others are off in the track.
  thumb = (index) => {
    const button = index === this.state.index ? this.slideButton(index) : null;
    return button ? button.querySelector("img") : null;
  };

  handleKeyDown = (event) => {
    if ("ArrowLeft" === event.key || "ArrowRight" === event.key) {
      event.preventDefault();
      this.choose(this.state.index + ("ArrowLeft" === event.key ? -1 : 1));
    }
  };

  handleBlur = (event) => {
    if (!this.root.current.contains(event.relatedTarget)) {
      this.setState({ focused: false });
    }
  };

  handlePointerDown = (event) => {
    this.swiped = false;
    this.downX = event.clientX;
  };

  handlePointerUp = (event) => {
    if (null === this.downX) {
      return;
    }
    const dx = event.clientX - this.downX;
    this.downX = null;
    if (Math.abs(dx) > SWIPE) {
      this.swiped = true;
      this.choose(this.state.index + (dx < 0 ? 1 : -1));
    }
  };

  render() {
    const { label } = this.props;
    const shots = this.shots();
    const { index, cycle, auto, viewOpen, viewIndex } = this.state;
    const running = this.running();
    const classes = ["fp-carousel", auto ? "fp-auto" : "", running ? "" : "fp-paused"].filter(Boolean).join(" ");
    return (
      <>
        <div className={classes} ref={this.root} tabIndex={0} role="region" aria-roledescription="carousel"
          aria-label={label} style={{ "--fp-dwell": `${DWELL}ms` }}
          onMouseEnter={() => this.setState({ hovered: true })} onMouseLeave={() => this.setState({ hovered: false })}
          onFocus={() => this.setState({ focused: true })} onBlur={this.handleBlur} onKeyDown={this.handleKeyDown}>
          {/* Only the picture swipes: a drag across the caption or the dots is not a choice of slide. */}
          <div className="fp-card-img fp-carousel-box" onPointerDown={this.handlePointerDown}
            onPointerUp={this.handlePointerUp} onPointerCancel={() => { this.downX = null; }}>
            <div className="fp-carousel-track" style={{ transform: `translateX(${-100 * index}%)` }}>
              {shots.map((shot, k) => (
                <div key={shot.name} className="fp-carousel-slide" role="group" aria-roledescription="slide"
                  aria-label={`${k + 1} of ${shots.length}`} aria-hidden={k !== index}>
                  <FrontShot name={shot.name} alt={shot.alt} loading={0 === k ? "eager" : "lazy"}
                    onOpen={() => this.open(k)} tabIndex={k === index ? 0 : -1} />
                </div>
              ))}
            </div>
            <button type="button" className="fp-carousel-arrow fp-carousel-prev" aria-label="Previous picture"
              onClick={() => this.choose(index - 1)}>&lsaquo;</button>
            <button type="button" className="fp-carousel-arrow fp-carousel-next" aria-label="Next picture"
              onClick={() => this.choose(index + 1)}>&rsaquo;</button>
          </div>
          <div className="fp-carousel-foot">
            {/* Announced when the slide changes, except while it turns by itself: a reader is not read a caption
                every six seconds. */}
            <p className="fp-carousel-caption" aria-live={running ? "off" : "polite"}>{shots[index].caption}</p>
            <div className="fp-carousel-dots">
              {shots.map((shot, k) => (
                <button key={shot.name} type="button" className={`fp-carousel-dot${k === index ? " fp-on" : ""}`}
                  aria-label={`Show picture ${k + 1} of ${shots.length}`}
                  aria-current={k === index ? "true" : undefined} onClick={() => this.choose(k)}>
                  {k === index && <span key={cycle} className="fp-carousel-fill" />}
                </button>
              ))}
            </div>
          </div>
        </div>
        <ThemedShotView show={viewOpen} items={shots} index={viewIndex}
          onIndex={(next) => this.setState({ viewIndex: next })} onHide={this.closeView} onClosed={this.focusSlide}
          getThumb={this.thumb} />
      </>
    );
  }
}
